import { useState } from 'react'
import { useIntl } from 'react-intl'
import { useDialogState } from 'ariakit/dialog'
import {
  IconArrowLineDown,
  Menu,
  MenuButton,
  MenuItem,
  Tooltip,
  useMenuState,
  useToast,
} from '@vtex/admin-ui'
import { type DownloadItemProps } from '@/components/products-listing/components/DownloadCollapser/DownloadListCollapser'
import { ExportModalMessages } from '@/components/products-listing/components/ExportModalMessages/ExportModalMessages'
import { useExportRequests } from '@/components/products-listing/hooks/useExportRequest'
import { useFeatureFlag } from '@/contexts/feature-flag'
import { useUserPermissionsContext } from '@/contexts/userPermissions'

import { ExportProductAndSKUDrawer } from '../../ExportProductAndSKUDrawer'
import { type SerializedFiltersData } from '../../filters'

import * as style from './ExportMenu.styles'

export type ExportDrawerContentProps = {
  total: number | undefined
  isFiltering: boolean
  exportData: ExportData
  onCreateExport: (newExport: DownloadItemProps) => void
}

export type ExportData = {
  BrandIds: SerializedFiltersData['FilterBrand']
  CategoryIds: number[]
  SalesChannelIds: SerializedFiltersData['FilterSalesChannel']
  SellerIds: SerializedFiltersData['FilterSeller']
  OutputFields?: string[]
  filterQueries: {
    creationDateFrom: string
    creationDateTo: string
    hasActiveKit: string
    isActive: string
    isVisible: string
    search: string
  }
}

export type ExportType =
  | 'Product'
  | 'Image'
  | 'ProductSpecification'
  | 'SkuSpecification'

export const ExportMenu = (props: ExportDrawerContentProps) => {
  const state = useMenuState()
  const { formatMessage } = useIntl()
  const limitModal = useDialogState()
  const { hasExportPermission } = useUserPermissionsContext()
  const showToast = useToast()
  const exportLimit = useFeatureFlag<number>('export_limit', 100000)

  const [open, setIsOpen] = useState(false)
  const [visibleTooltip, setVisibleTooltip] = useState(false)

  const { postExportRequest } = useExportRequests({
    exportData: props.exportData,
    onCreateExport: props.onCreateExport,
  })

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleSelectType = (exportType: ExportType) => {
    if (props.total === 0) {
      showToast({
        variant: 'warning',
        duration: 10000,
        message: formatMessage({
          id: 'admin/admin.catalog.export-empty-alert.message',
          defaultMessage:
            'The current export selection is empty. Try using different terms or filters.',
        }),
      })
      return
    }

    if (props.total && props.total > exportLimit) {
      limitModal.show()
      return
    }

    if (exportType === 'Product') {
      return setIsOpen(true)
    }

    return postExportRequest(exportType)
  }

  return (
    <>
      <Tooltip
        placement="top"
        visible={visibleTooltip && !hasExportPermission}
        text={formatMessage({
          id: 'admin/admin.catalog.import-export-menu.permission-error',
          defaultMessage:
            "Your user role doesn't have permission to perform this action. Contact your account administrator.",
        })}
      >
        <div
          onMouseEnter={() => {
            setVisibleTooltip(true)
          }}
          onMouseLeave={() => {
            setVisibleTooltip(false)
          }}
        >
          <MenuButton
            state={state}
            variant="neutralTertiary"
            disabled={!hasExportPermission}
            label={
              <>
                <IconArrowLineDown aria-hidden />
                {formatMessage({
                  id: 'admin/admin.catalog.export-menu.export-button-label',
                  defaultMessage: 'Export',
                })}
              </>
            }
            className={style.menuButton}
          />
        </div>
      </Tooltip>
      <Menu state={state} aria-label="actions" className={style.menuActions}>
        <MenuItem
          label={formatMessage({
            id: 'admin/admin.catalog.import-export-menu.products-and-skus-label',
            defaultMessage: 'Products and SKUs',
          })}
          onClick={() => handleSelectType('Product')}
        />
        <MenuItem
          label={formatMessage({
            id: 'admin/admin.catalog.import-export-menu.images-label',
            defaultMessage: 'Images',
          })}
          onClick={() => handleSelectType('Image')}
        />
        <MenuItem
          label={formatMessage({
            id: 'admin/admin.catalog.import-export-menu.product-specifications-label',
            defaultMessage: 'Product Specifications',
          })}
          onClick={() => handleSelectType('ProductSpecification')}
        />
        <MenuItem
          label={formatMessage({
            id: 'admin/admin.catalog.import-export-menu.sku-specifications-label',
            defaultMessage: 'SKU Specifications',
          })}
          onClick={() => handleSelectType('SkuSpecification')}
        />
      </Menu>
      <ExportProductAndSKUDrawer
        open={open}
        handleClose={handleClose}
        exportData={props.exportData}
        onCreateExport={props.onCreateExport}
      />
      <ExportModalMessages limitModal={limitModal} exportLimit={exportLimit} />
    </>
  )
}
